import React from "react";
import {
  Autocomplete,
  createFilterOptions,
  type FilterOptionsState,
  TextField,
} from "@mui/material";

type AutoCompleteOption = string | { inputValue: string; title: string };

const filter = createFilterOptions<AutoCompleteOption>();

export default function AutoCompleteInput({
  label,
  state,
  setState,
  options,
  deleting,
  required = false,
}: {
  label: string;
  state: string | null;
  setState: (value: string) => void;
  options: string[];
  deleting: boolean;
  required?: boolean;
}) {
  function handleOnChange(
    _event: React.SyntheticEvent,
    newValue: AutoCompleteOption | null,
  ) {
    if (typeof newValue === "string") {
      setState(newValue);
    } else if (newValue?.inputValue) {
      // Create a new value from the user input
      setState(newValue.inputValue);
    }
  }

  function filterOptions(
    options: AutoCompleteOption[],
    params: FilterOptionsState<AutoCompleteOption>,
  ): AutoCompleteOption[] {
    const filtered = filter(options, params);
    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option);
    const inputValueTrimmed = inputValue.trim();
    if (inputValueTrimmed !== "" && !isExisting) {
      filtered.push({
        inputValue: inputValueTrimmed,
        title: `${inputValueTrimmed} (add new)`,
      });
    }
    return filtered;
  }

  return (
    <Autocomplete<AutoCompleteOption, false, false, true>
      size="small"
      fullWidth
      value={state}
      onChange={handleOnChange}
      filterOptions={filterOptions}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={options}
      disabled={deleting}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // Option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        return "";
      }}
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
        option,
      ) => {
        const { key, ...optionProps } = props;
        return (
          <li {...optionProps} key={key}>
            {typeof option === "string" ? option : option.title}
          </li>
        );
      }}
      freeSolo
      renderInput={(params) => (
        <TextField {...params} required={required} label={label} />
      )}
    />
  );
}
